import { useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  Typography,
  Divider,
  Button,
  Link,
  TextField,
  Box,
  useTheme,
} from '@mui/material';
import {
  DataGridPro,
  GridActionsCellItem,
  GridColDef,
  GridEventListener,
  GridRowEditStopReasons,
  GridRowId,
  GridRowModel,
  GridRowModes,
  GridRowModesModel,
  GridRowSelectionModel,
  GridSlotProps,
  GridToolbarContainer,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { FormikProps } from 'formik';
import type {
  Item,
  OrderCreateInput,
  OrderItem,
  PurchaseOrder,
  Vendor,
} from '@repo/api-contract';
import { pushToast, useDispatch, useSelector } from '../../../../state';
import { useTranslation } from 'react-i18next';
import React from 'react';
import { openSearchItemModal } from '../../../../state/itemSearchModalSlice';
import {
  closeModal,
  openOrderModal,
  setDisableButtons,
  setLoading,
} from '../../../../state/orderModalSlice';
import hexToRGBA from '../../../../util/converters/hexToRGBA';
import { getFormikField } from '../../../../util/fornik-helpers';
import { openNewItemModal } from '../../../../state/itemModalSlice';
import { createVendorColumn } from '../../../utils/vendorColumn';
import { client } from '../../../../util';
import { TRPCClientError } from '@trpc/client';
import { serverError } from '../../constants/errors';
import { createPurchaseOrderColumn } from '../../../utils/purchaseOrderColumn';
import BulkActionsMenu from '../../../utils/OrderActionsMenu';
import { openPurchaseOrderModal } from '../../../../state/purchaseOrderModalSlice';
import { openSearchPurchaseOrderModal } from '../../../../state/purchaseOrderSearchModalSlice';
import { openSearchVendorModal } from '../../../../state/vendorSearchModalSlice';
import { openNewVendorModal } from '../../../../state/vendorModalSlice';
import {
  EMPTY_LEADING_GRID_COLUMNS,
  GRID_CHECKBOX_COLUMN_ORDER,
  usePersistedDataGridColumnPreferences,
} from '../../../../util/hooks';
import type { DataGridTableId } from '../../../../util/dataGridTableIds';
import { GRID_CHECKBOX_SELECTION_FIELD } from '@mui/x-data-grid';
import { DataGridTranslatedColumnHeader } from '../../../dataGrid/DataGridTranslatedColumnHeader';

const TABLE_ID = 'sales-order-items' satisfies DataGridTableId;
const GLOBAL_COMMON_COL_I18N = 'team.Global.Common';
const GLOBAL_BUTTONS_COL_I18N = 'team.Global.Buttons';
const ADD_SALES_ORDER_COL_I18N =
  'team.Modals.Sales Orders.createAndEdit.Add Sales Order';
const columnPadding = '1 2px';

export default function Items({
  formik,
  viewMode,
}: {
  formik: FormikProps<OrderCreateInput>;
  viewMode: boolean;
}): JSX.Element {
  const { t: tAddO } = useTranslation([], {
    keyPrefix: 'team.Modals.Sales Orders.createAndEdit.Add Sales Order',
  });

  const theme = useTheme();
  const dispatch = useDispatch();

  const { isOpenOrderModal, formData, disableButtons } = useSelector(
    ({ orderModalSlice }) => orderModalSlice,
  );

  const [items, setItems] = useState<OrderItem[]>([]);
  const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>(
    {},
  );
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>(
    [],
  );
  const apiRef = useGridApiRef();

  const toggleDisableButtons = (value: boolean) => {
    dispatch(setDisableButtons(value));
  };

  const genUUID = () => {
    let uuid = uuidv4();
    return uuid;
  };

  // ----------------- Items functions (Import and Create) -----------------
  const onImport = async (selectedItems: Item[]) => {
    const currentItemIds = new Set(formData.items.map((item) => item.itemId));
    // Map Items -> OrderItem rows we use in the grid
    const mappedOrderItems = selectedItems
      .map((item) => {
        return {
          description: item.details.description,
          quantityOrdered: 0,
          quantityShipped: 0,
          unitNumber: item.details.unit,
          itemId: item.ItemId,
          taxable: item.details.taxable,
          vendorItemNumber: item.details.vendorItemNumber,
          customerItemNumber: item.details.customerItemNumber || '',
          vendorCost: item.details.vendorCost ?? 0,
          customerPrice: item.details.customerPrice,
          commissionable: item.details.commissionable,
          commissionRate: item.details.commissionRate,
          primaryVendor: item.details.primaryVendor || null,
          loadingV: !!item.details.primaryVendor || false,
          purchaseOrder: null,
          loadingPO: false,
        };
      })
      .filter((orderItem) => !currentItemIds.has(orderItem.itemId)); // Exclude duplicates

    // Merge into form right away so user sees rows instantly
    const initialItems = [...formik.values.items, ...mappedOrderItems];
    formik.setFieldValue('items', initialItems);

    // open modal
    dispatch(openOrderModal());

    // figure out which of the NEW rows actually need vendor fetch
    const rowsNeedingVendor = mappedOrderItems.filter(
      (row) => !!row.primaryVendor,
    );

    if (rowsNeedingVendor.length === 0) {
      // nothing async to do
      return;
    }
    // Disable save buttons globally while we're resolving vendors
    toggleDisableButtons(true);
    // We'll mutate this local array and push it back into formik each time
    let workingItems = [...initialItems];
    // update one row in workingItems and then sync formik
    const applyRowUpdate = (rowId: string, patch: any) => {
      workingItems = workingItems.map((existing) => {
        if (existing.itemId === rowId) {
          return {
            ...existing,
            ...patch,
          };
        }
        return existing;
      });
      // push the latest full array to formik
      formik.setFieldValue('items', workingItems);
    };
    // kick off all fetches in parallel
    await Promise.all(
      rowsNeedingVendor.map(async (row) => {
        try {
          const vendorData = await client.vendorById.query(
            row.primaryVendor as string,
          );
          // success -> replace vendor UUID with vendor object + stop spinner
          applyRowUpdate(row.itemId, {
            primaryVendor: vendorData,
            loadingV: false,
          });
        } catch (error) {
          // error -> toast + stop spinner + null vendor
          if (error instanceof TRPCClientError) {
            dispatch(pushToast({ message: error.message, type: 'error' }));
          } else {
            dispatch(pushToast({ message: serverError, type: 'error' }));
          }
          applyRowUpdate(row.itemId, {
            primaryVendor: null,
            loadingV: false,
          });
        }
      }),
    );

    // all vendor fetches done -> enable buttons again
    toggleDisableButtons(false);
  };

  const newItemCallBack = async (item: Item | undefined): Promise<void> => {
    // open modal right away so user sees the row
    dispatch(openOrderModal());
    if (!item) return;

    // build the new row (same shape as onImport)
    const mappedItem = {
      description: item.details.description,
      quantityOrdered: 0,
      quantityShipped: 0,
      unitNumber: item.details.unit,
      itemId: item.ItemId,
      taxable: item.details.taxable,
      vendorItemNumber: item.details.vendorItemNumber,
      customerItemNumber: item.details.customerItemNumber || '',
      vendorCost: item.details.vendorCost ?? 0,
      customerPrice: item.details.customerPrice,
      commissionable: item.details.commissionable,
      commissionRate: item.details.commissionRate,
      primaryVendor: item.details.primaryVendor || null, // vendor UUID or null
      loadingV: !!item.details.primaryVendor || false, // row spinner
      purchaseOrder: null,
      loadingPO: false,
    };

    // show it in UI immediately
    const initialItems = [...formik.values.items, mappedItem];
    formik.setFieldValue('items', initialItems);

    // no vendor UUID? we're done
    if (!mappedItem.primaryVendor) {
      return;
    }

    // we DO have a vendor to fetch -> disable save while we resolve
    toggleDisableButtons(true);

    // we'll keep/edit one local working array and re-sync to formik,
    // same pattern we used in onImport
    let workingItems = [...initialItems];

    const applyRowUpdate = (rowId: string, patch: any) => {
      workingItems = workingItems.map((existing) => {
        if (existing.itemId === rowId) {
          return {
            ...existing,
            ...patch,
          };
        }
        return existing;
      });
      formik.setFieldValue('items', workingItems);
    };

    try {
      const vendorData = await client.vendorById.query(
        mappedItem.primaryVendor as string,
      );

      applyRowUpdate(mappedItem.itemId, {
        primaryVendor: vendorData, // swap UUID -> vendor object
        loadingV: false,
      });
    } catch (error) {
      if (error instanceof TRPCClientError) {
        dispatch(pushToast({ message: error.message, type: 'error' }));
      } else {
        dispatch(pushToast({ message: serverError, type: 'error' }));
      }
      applyRowUpdate(mappedItem.itemId, {
        primaryVendor: null,
        loadingV: false,
      });
    } finally {
      toggleDisableButtons(false);
    }
  };

  // ----------------- PurchaseOrder Bulk functions -----------------
  const handleBulkBrowsePurchaseOrder = () => {
    if (viewMode) return;
    if (!selectionModel.length) return;

    const selectedIds = new Set(selectionModel);

    const onImport = (pickedList: PurchaseOrder[]) => {
      const picked = pickedList?.[0];
      // re-open order modal (grid remount)
      dispatch(openOrderModal());
      if (!picked) return;

      const current = formik.values.items || [];
      const nextItems = current.map((it: any) =>
        selectedIds.has(it.itemId) ? { ...it, purchaseOrder: picked } : it,
      );

      setItems(nextItems);
      formik.setFieldValue('items', nextItems);
      setSelectionModel([]);
    };

    const onCancel = () => {
      dispatch(openOrderModal());
    };

    dispatch(closeModal());
    dispatch(
      openSearchPurchaseOrderModal({
        onImport,
        onCancel,
        multiSelect: false,
      }),
    );
  };

  const handleBulkCreatePurchaseOrder = () => {
    if (viewMode) return;
    if (!selectionModel.length) return;

    const selectedIds = new Set(selectionModel);

    // Build items[] for the new Purchase Order from selected rows
    const allItems = formik.values.items || [];
    const selectedRows = allItems.filter((it: any) =>
      selectedIds.has(it.itemId),
    );

    if (!selectedRows.length) return;
    const poItems = selectedRows.map((row: any) => ({
      itemId: row.itemId,
      description: row.description ?? '',
      quantityOrdered: row.quantityOrdered ?? 0,
      quantityShipped: row.quantityShipped ?? 0,
      unitNumber: row.unitNumber ?? '',
      taxable: row.taxable ?? true,
      vendorItemNumber: row.vendorItemNumber ?? '',
      customerItemNumber: row.customerItemNumber ?? '',
      vendorCost: row.vendorCost ?? 0,
      customerPrice: row.customerPrice ?? 0,
      commissionable: row.commissionable ?? false,
      commissionRate: row.commissionRate ?? 0,
      primaryVendor: row.primaryVendor ?? null,
      loadingV: row.loadingV ?? false,
    }));

    const createCallback = async (po: PurchaseOrder | undefined) => {
      // back to order modal after create
      dispatch(openOrderModal());
      if (!po) return;

      const current = formik.values.items || [];
      const nextItems = current.map((it: any) =>
        selectedIds.has(it.itemId) ? { ...it, purchaseOrder: po } : it,
      );

      setItems(nextItems);
      formik.setFieldValue('items', nextItems);
      setSelectionModel([]);
    };

    dispatch(closeModal());
    dispatch(
      openPurchaseOrderModal({
        createCallback,
        resetState: true,
        formData: {
          orderId: '',
          code: '',
          rate: 0,
          orderDate: 0,
          estimatedShipDate: 0,
          customerDeposit: 0,
          shipMethod: '',
          deadline: 0,
          items: poItems,
          inHandsDate: 0,
          vendorName: '',
          vendorAddress: '',
          vendorAccountNumber: '',
          shipToName: '',
          shipToAddress: '',
          notes: '',
          paymentTerms: [],
        },
      }),
    );
  };

  // ----------------- Vendor Bulk functions -----------------
  const handleBulkBrowseVendor = () => {
    if (viewMode) return;
    if (!selectionModel.length) return;

    const selectedIds = new Set(selectionModel);

    const onImport = (vendors: Vendor[]) => {
      const picked = vendors?.[0];
      // re-open order modal (grid remount)
      dispatch(openOrderModal());
      if (!picked) return;

      const current = formik.values.items || [];
      const nextItems = current.map((it: any) =>
        selectedIds.has(it.itemId)
          ? {
              ...it,
              primaryVendor: picked,
              // same reset logic as createVendorColumn.applyToFormik
              vendorItemNumber: '',
              customerItemNumber: '',
              purchaseOrder: '',
              loadingV: false,
            }
          : it,
      );

      setItems(nextItems);
      formik.setFieldValue('items', nextItems);
      setSelectionModel([]);
    };

    const onCancel = () => {
      dispatch(openOrderModal());
    };

    dispatch(closeModal());
    dispatch(
      openSearchVendorModal({
        onImport,
        onCancel,
        multiSelect: false,
      }),
    );
  };

  const handleBulkCreateVendor = () => {
    if (viewMode) return;
    if (!selectionModel.length) return;

    const selectedIds = new Set(selectionModel);

    const createCallback = async (vendor: Vendor | undefined) => {
      // back to order modal after create
      dispatch(openOrderModal());
      if (!vendor) return;

      const current = formik.values.items || [];
      const nextItems = current.map((it: any) =>
        selectedIds.has(it.itemId)
          ? {
              ...it,
              primaryVendor: vendor,
              vendorItemNumber: '',
              customerItemNumber: '',
              purchaseOrder: '',
              loadingV: false,
            }
          : it,
      );

      setItems(nextItems);
      formik.setFieldValue('items', nextItems);
      setSelectionModel([]);
    };

    dispatch(closeModal());
    dispatch(
      openNewVendorModal({
        createCallback,
      }),
    );
  };

  // Adds empty row to the table
  const handleAddRowClick = () => {
    const tempId = `Custom-${genUUID()}`;
    const newRow = {
      itemId: tempId,
      description: '',
      quantityOrdered: 0,
      quantityShipped: 0,
      unitNumber: '',
      taxable: true,
      vendorItemNumber: '',
      customerItemNumber: '',
      vendorCost: 0,
      customerPrice: 0,
      commissionable: false,
      commissionRate: 0.0,
    };
    // This is stupid but the reason we are setting Items directly and not through formik
    // is because formik is slow and setRowModesModel will give error of unfound record
    setItems([...formik.values.items, newRow]);
    formik.setFieldValue('items', [...formik.values.items, newRow]);

    setRowModesModel((prevModel) => ({
      ...prevModel,
      [tempId]: { mode: GridRowModes.Edit },
    }));
  };

  // ----------------- EditToolbar -----------------
  const onCancel = () => {
    dispatch(openOrderModal());
  };

  const handleImportClick = () => {
    dispatch(closeModal());
    dispatch(openSearchItemModal({ onImport, onCancel }));
  };

  const handleCreateClick = () => {
    dispatch(closeModal());
    dispatch(openNewItemModal({ createCallback: newItemCallBack }));
  };

  function EditToolbar(props: {
    selectionModel: GridRowSelectionModel;
    setSelectionModel: (m: GridRowSelectionModel) => void;
    items: any[];
    setItems: React.Dispatch<React.SetStateAction<any[]>>;
    formik: any;
    viewMode: boolean;
    onAddRow: () => void;
    bulkBrowsePurchaseOrder: () => void;
    bulkCreatePurchaseOrder: () => void;
    bulkBrowseVendor: () => void;
    bulkCreateVendor: () => void;
  }) {
    const {
      selectionModel,
      setSelectionModel,
      items,
      setItems,
      formik,
      viewMode,
      onAddRow,
      bulkBrowsePurchaseOrder,
      bulkCreatePurchaseOrder,
      bulkBrowseVendor,
      bulkCreateVendor,
    } = props;

    return (
      <GridToolbarContainer sx={{ justifyContent: 'flex-end' }}>
        <BulkActionsMenu
          selectedIds={selectionModel}
          clearSelection={() => setSelectionModel([])}
          items={items}
          setItems={setItems}
          formik={formik}
          viewMode={viewMode}
          onAddRow={onAddRow}
          importItemClick={handleImportClick}
          createItemClick={handleCreateClick}
          handleBulkBrowsePurchaseOrder={bulkBrowsePurchaseOrder}
          handleBulkCreatePurchaseOrder={bulkCreatePurchaseOrder}
          handleBulkBrowseVendor={bulkBrowseVendor}
          handleBulkCreateVendor={bulkCreateVendor}
        />
      </GridToolbarContainer>
    );
  }

  // ----------------- TABLE -----------------
  const handleRowEditStop: GridEventListener<'rowEditStop'> = (
    params,
    event,
  ) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleEditClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleDeleteClick = (id: GridRowId) => () => {
    formik.setFieldValue(
      'items',
      items.filter((item) => item.itemId !== id),
    );
    setItems(items.filter((item) => item.itemId !== id));
  };

  const handleCancelClick = (id: GridRowId) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });
  };

  const processRowUpdate = (newRow: GridRowModel) => {
    // Find the original row in items
    const originalRow = items.find((item) => item.itemId === newRow.id);

    if (!originalRow) {
      throw new Error('Original row not found.');
    }

    // Merge the original row with the updated data
    const updatedRow = {
      ...originalRow,
      ...newRow, // Update fields from newRow
      isNew: false, // Ensure isNew is set to false
    };

    const updatedItems = items.map((item) =>
      item.itemId === newRow.id ? updatedRow : item,
    );

    // Update the state with the modified row
    setItems(updatedItems);

    // Update Formik with the updated items
    formik.setFieldValue('items', updatedItems);

    return updatedRow;
  };

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  // It does not let user to enter negative values and converts string to number so formik does not send string in api call
  const numberTypeController = (value: any, row: any) => {
    return value < 0 ? 0 : parseFloat(value);
  };

  const baseColumns: GridColDef[] = useMemo(
    () => [
      createVendorColumn({
        field: 'primaryVendor',
        translatedHeader: {
          keyPrefix: GLOBAL_COMMON_COL_I18N,
          labelKey: 'Vendor',
        },
        formik: formik,
        applyToFormik: ({ formik, picked, gridId }) => {
          // find index of the row in Formik by id/itemId
          const idx = formik.values.items.findIndex(
            (it: any) => it.itemId === gridId || it.id === gridId,
          );
          if (idx < 0) return;
          // get the current row
          const row = formik.values.items[idx];
          // build updated row
          const updatedRow = {
            ...row,
            primaryVendor: picked,
            vendorItemNumber: '',
            customerItemNumber: '',
            purchaseOrder: '',
          };
          // build new items array (immutably)
          const updatedItems = [
            ...formik.values.items.slice(0, idx),
            updatedRow,
            ...formik.values.items.slice(idx + 1),
          ];
          // now set entire items array ONCE
          formik.setFieldValue('items', updatedItems);
        },
      }),
      createPurchaseOrderColumn({
        field: 'purchaseOrder',
        translatedHeader: {
          keyPrefix: GLOBAL_COMMON_COL_I18N,
          labelKey: 'Purchase Order',
        },
        formik,
        applyToFormik: ({ formik, picked, gridId }) => {
          // find index of the row in Formik by id/itemId
          const idx = formik.values.items.findIndex(
            (it: any) => it.itemId === gridId || it.id === gridId,
          );
          if (idx < 0) return;
          // get the current row
          const row = formik.values.items[idx];
          // build updated row
          const updatedRow = {
            ...row,
            purchaseOrder: picked,
          };
          // build new items array (immutably)
          const updatedItems = [
            ...formik.values.items.slice(0, idx),
            updatedRow,
            ...formik.values.items.slice(idx + 1),
          ];
          // now set entire items array ONCE
          formik.setFieldValue('items', updatedItems);
        },
      }),
      {
        field: 'quantityOrdered',
        headerName: 'Qty. Ordered',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Qty. Ordered"
            params={params}
            keyPrefix={ADD_SALES_ORDER_COL_I18N}
          />
        ),
        editable: true,
        type: 'number',
        flex: 1,
        align: 'left',
        headerAlign: 'left',
        valueParser: numberTypeController,
      },
      {
        field: 'quantityShipped',
        headerName: 'Qty. Shipped',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Qty. Shipped"
            params={params}
            keyPrefix={ADD_SALES_ORDER_COL_I18N}
          />
        ),
        editable: true,
        type: 'number',
        flex: 1,
        align: 'left',
        headerAlign: 'left',
        valueParser: numberTypeController,
      },
      {
        field: 'unitNumber',
        headerName: 'Unit',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Unit"
            params={params}
            keyPrefix={ADD_SALES_ORDER_COL_I18N}
          />
        ),
        editable: true,
        flex: 0.5,
      },
      {
        field: 'vendorItemNumber',
        headerName: 'Vendor Item No.',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Vendor Item No."
            params={params}
            keyPrefix={ADD_SALES_ORDER_COL_I18N}
          />
        ),
        editable: true,
        flex: 1,
      },
      {
        field: 'customerItemNumber',
        headerName: 'Customer Item No.',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Customer Item No."
            params={params}
            keyPrefix={ADD_SALES_ORDER_COL_I18N}
          />
        ),
        editable: true,
        flex: 1,
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'description',
        headerName: 'Description',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Description"
            params={params}
            keyPrefix={GLOBAL_COMMON_COL_I18N}
          />
        ),
        editable: true,
        flex: 1,
      },
      {
        field: 'commissionable',
        headerName: 'Commissionable',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Commissionable"
            params={params}
            keyPrefix={GLOBAL_COMMON_COL_I18N}
          />
        ),
        type: 'boolean',
        editable: true,
        flex: 1.2,
      },
      {
        field: 'commissionRate',
        headerName: 'Commission Rate',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Commission Rate"
            params={params}
            keyPrefix={GLOBAL_COMMON_COL_I18N}
          />
        ),
        type: 'number',
        editable: true,
        flex: 1.2,
        valueParser: (value) => {
          const num = Number(value);
          if (isNaN(num)) return undefined;
          return Math.max(0, Math.min(1, num));
        },
      },
      {
        field: 'taxable',
        headerName: 'Taxable',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Taxable"
            params={params}
            keyPrefix={GLOBAL_COMMON_COL_I18N}
          />
        ),
        type: 'boolean',
        editable: true,
        flex: 1,
      },
      {
        field: 'vendorCost',
        headerName: 'Cost',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Cost"
            params={params}
            keyPrefix={ADD_SALES_ORDER_COL_I18N}
          />
        ),
        editable: true,
        type: 'number',
        flex: 1,
        headerAlign: 'left',
        valueParser: numberTypeController,
      },
      {
        field: 'customerPrice',
        headerName: 'Price',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Price"
            params={params}
            keyPrefix={ADD_SALES_ORDER_COL_I18N}
          />
        ),
        editable: true,
        type: 'number',
        flex: 1,
        headerAlign: 'left',
        valueParser: numberTypeController,
      },
      {
        field: 'vendorAmount',
        headerName: 'Vendor Amount',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Vendor Amount"
            params={params}
            keyPrefix={ADD_SALES_ORDER_COL_I18N}
          />
        ),
        flex: 1,
        editable: false,
        type: 'number',
        align: 'right',
        headerAlign: 'left',
        valueGetter: (value, row) => {
          const vendorCost = row.vendorCost ?? 0;
          const quantityOrdered = row.quantityOrdered ?? 0;
          return vendorCost * quantityOrdered;
        },
      },
      {
        field: 'customerAmount',
        headerName: 'Customer Amount',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Customer Amount"
            params={params}
            keyPrefix={ADD_SALES_ORDER_COL_I18N}
          />
        ),
        flex: 1,
        editable: false,
        type: 'number',
        align: 'right',
        headerAlign: 'left',
        valueGetter: (value, row) => {
          const customerPrice = row.customerPrice ?? 0;
          const quantityOrdered = row.quantityOrdered ?? 0;
          return customerPrice * quantityOrdered;
        },
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Actions',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Actions"
            params={params}
            keyPrefix={GLOBAL_BUTTONS_COL_I18N}
          />
        ),
        width: 80,
        cellClassName: 'actions',
        getActions: ({ id }) => {
          const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

          if (isInEditMode) {
            return [
              <GridActionsCellItem
                disabled={viewMode}
                icon={<SaveIcon />}
                label="Save"
                sx={{
                  color: 'primary.main',
                }}
                onClick={handleSaveClick(id)}
              />,
              <GridActionsCellItem
                disabled={viewMode}
                icon={<CancelIcon />}
                label="Cancel"
                className="textPrimary"
                onClick={handleCancelClick(id)}
                color="inherit"
              />,
            ];
          }

          return [
            <GridActionsCellItem
              key="edit"
              disabled={viewMode || disableButtons}
              icon={<EditIcon />}
              label="Edit"
              className="textPrimary"
              onClick={handleEditClick(id)}
              color="inherit"
            />,
            <GridActionsCellItem
              key="delete"
              disabled={viewMode || disableButtons}
              icon={<DeleteIcon />}
              label="Delete"
              onClick={handleDeleteClick(id)}
              color="inherit"
            />,
          ];
        },
      },
    ],
    [
      formik,
      rowModesModel,
      viewMode,
      disableButtons,
      handleSaveClick,
      handleCancelClick,
      handleEditClick,
      handleDeleteClick,
      numberTypeController,
    ],
  );

  const persistedPrefs = usePersistedDataGridColumnPreferences({
    tableId: TABLE_ID,
    columns: baseColumns,
    apiRef,
    leadingGridColumnFields: viewMode
      ? EMPTY_LEADING_GRID_COLUMNS
      : GRID_CHECKBOX_COLUMN_ORDER,
  });

  const columns = useMemo(
    () => persistedPrefs.applyPersistedPreferences(baseColumns),
    [baseColumns, persistedPrefs.applyPersistedPreferences],
  );

  useEffect(() => {
    setItems(formik.values.items);
  }, [isOpenOrderModal, formik.values.items]);

  const totalVendorCost = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(
    items.reduce(
      (acc, item) => acc + item.vendorCost * item.quantityOrdered,
      0,
    ),
  );

  const totalCustomerPrice = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(
    items.reduce(
      (acc, item) => acc + item.customerPrice * item.quantityOrdered,
      0,
    ),
  );

  return (
    <>
      <Typography
        color="primary"
        className="flex justify-between"
        component="div"
      >
        <span>{tAddO('ITEMS')}</span>
        <Box className="flex">
          <Link className="mr-2" color="text.secondary" underline="none">
            {tAddO('TOTAL COST') + ':'}
          </Link>
          <span className="font-bold text-black">{'$' + totalVendorCost}</span>
          <Link className="mx-2" color="text.secondary" underline="none">
            {tAddO('TOTAL PRICE') + ':'}
          </Link>
          <span className="font-bold text-black">
            {'$' + totalCustomerPrice}
          </span>
        </Box>
      </Typography>
      <Divider sx={{ marginBottom: 1 }} />
      <Box sx={{ height: 500, width: '100%', overflowX: 'auto' }}>
        <DataGridPro
          apiRef={apiRef}
          columnHeaderHeight={56}
          disableRowSelectionOnClick
          disableColumnMenu={viewMode}
          disableDensitySelector={viewMode}
          disableColumnSelector={viewMode}
          disableMultipleRowSelection={viewMode}
          isCellEditable={() => !viewMode}
          checkboxSelection={!viewMode}
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={(newSelection) =>
            setSelectionModel(newSelection)
          }
          sx={{
            border: 0,
            '& .MuiDataGrid-cell': {
              padding: columnPadding,
            },
            '& .MuiDataGrid-columnHeader': {
              padding: columnPadding,
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              whiteSpace: 'normal',
              lineHeight: 1.2,
            },
          }}
          onRowEditStop={handleRowEditStop}
          hideFooter
          disableColumnFilter
          rowModesModel={rowModesModel}
          onRowModesModelChange={handleRowModesModelChange}
          processRowUpdate={processRowUpdate}
          slots={{
            toolbar: (props) => (
              <EditToolbar
                {...props}
                selectionModel={selectionModel}
                setSelectionModel={setSelectionModel}
                items={items}
                setItems={setItems}
                formik={formik}
                viewMode={viewMode}
                onAddRow={handleAddRowClick}
                bulkBrowsePurchaseOrder={handleBulkBrowsePurchaseOrder}
                bulkCreatePurchaseOrder={handleBulkCreatePurchaseOrder}
                bulkBrowseVendor={handleBulkBrowseVendor}
                bulkCreateVendor={handleBulkCreateVendor}
              />
            ),
          }}
          rows={items.map((item) => ({
            ...item,
            id: item.itemId,
          }))}
          columns={columns}
          columnVisibilityModel={persistedPrefs.columnVisibilityModel}
          onColumnVisibilityModelChange={
            persistedPrefs.onColumnVisibilityModelChange
          }
          onColumnWidthChange={persistedPrefs.onColumnWidthChange}
          onColumnOrderChange={persistedPrefs.onColumnOrderChange}
          pinnedColumns={{
            right: ['actions'],
            ...(!viewMode ? { left: [GRID_CHECKBOX_SELECTION_FIELD] } : {}),
          }}
          initialState={{
            pagination: { paginationModel: { pageSize: 100 } },
          }}
          pageSizeOptions={[items?.length || 0]}
        />
      </Box>
      <Divider sx={{ marginY: 3 }} />
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <TextField
          disabled={viewMode}
          variant="filled"
          label={tAddO('Special Instructions')}
          multiline
          rows={4}
          fullWidth
          sx={{
            backgroundColor: hexToRGBA(theme.palette.primary.main, 0.08),
          }}
          name="specialInstructions"
          error={Boolean(getFormikField(formik, 'specialInstructions').error)}
          helperText={getFormikField(formik, 'specialInstructions').error}
          value={getFormikField(formik, 'specialInstructions').value}
          onChange={formik.handleChange}
        />
      </Box>
    </>
  );
}
